import { useCallback, useEffect, useMemo, useState } from 'react';
import { Edit } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useDashboards } from '../hooks/useDashboards';
import { useDashboardLayouts } from '../hooks/useDashboardLayouts';
import { useWorkspace } from '@/features/Workspaces/state/WorkspaceContext';
import { useViewScope } from '@/shared/hooks/use-view-scope';
import { useTour, TourService } from '@/features/Tour';
import { NoWorkspacePlaceholder } from '@/features/Workspaces/components/NoWorkspacePlaceholder';
import { DashboardGrid } from './DashboardGrid';
import { DashboardSkeleton } from './DashboardSkeleton';
import { DashboardEmptyState } from './DashboardEmptyState';
import { AddDashboardFlyout, SlotContext } from './AddDashboardFlyout';
import { LayoutSwitcher } from './LayoutSwitcher';
import { DashboardTabBar } from './DashboardTabBar';
import { useDashboardTabs } from '../hooks/useDashboardTabs';
import { resolveSlotDashboards, findFirstFitPosition, SIZE_TO_W, SIZE_TO_H } from '../utils/layoutHelpers';
import { getFormFactor } from '../utils/formFactor';
import { Dashboard, DashboardSize } from '../types/Dashboard';
import { isSuggestedLayoutId } from '../data/suggestedLayouts';
import { DashboardDragProvider } from '../state/DashboardDragContext';
import { LayoutSlot } from '../types/Layout';
import { useToast } from '@/shared/hooks/use-toast';
import { DashboardService } from '../services/dashboardService';
import { useAuth } from '@/features/Auth/state/AuthContext';
import { useSuperAdminOrg } from '@/shared/contexts/SuperAdminOrgContext';

const DashboardViewBody = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentWorkspace, products } = useWorkspace();
  const { scope: escopoAtivo } = useViewScope();
  /**
   * Painel não existe no nível PESSOAL (AB#85637), e a rota dele recusa aquele
   * escopo — este componente não chega a renderizar ali. O mapeamento existe
   * porque o tipo do escopo ganhou um valor que os serviços de painel não
   * conhecem, e escolher em silêncio seria pior do que dizer isto aqui.
   */
  const viewScope = escopoAtivo === 'user' ? 'organization' : escopoAtivo;
  const { user } = useAuth();
  const tour = useTour();
  const showTourModal = tour?.showTourModal;

  // Dispara o tour de boas-vindas no primeiro acesso da org: quando o usuário
  // (pós-onboarding) cai na dashboard ainda sem workspace. O DashboardView só
  // renderiza pós-onboarding, então o trigger não aparece durante o setup.
  // (Regressão #83778: este bloco foi removido por engano no PR 12705.)
  // Convidado (bug 84095): a org já tem produtos, então o gate "org vazia" não
  // cobre — a flag de sessão marcada no fecho do onboarding dispara o splash.
  useEffect(() => {
    if (!showTourModal) return;
    if (products.length > 0 && !TourService.hasWelcomeTourPending()) return;
    const timer = setTimeout(() => {
      TourService.consumeWelcomeTourPending();
      showTourModal();
    }, 500);
    return () => clearTimeout(timer);
  }, [products.length, showTourModal]);
  const { selectedOrganization } = useSuperAdminOrg();
  // Mesmo cálculo de scopeContext usado em useDashboards/useDashboardLayouts:
  // sem isso, chamadas inline a DashboardService.addDashboard caem no path
  // legado (flat root) e ignoram byScope.
  const effectiveOrgId = selectedOrganization?.id ?? user?.organizationId ?? undefined;
  const scopeContext = {
    scope: viewScope,
    workspaceId: currentWorkspace?.id,
    orgId:
      viewScope === 'organization' || viewScope === 'product' ? effectiveOrgId : undefined,
  };
  const { currentTabId } = useDashboardTabs();
  const { dashboards, isLoading, addDashboard, updateDashboard, removeDashboard, reorderDashboards } = useDashboards(
    currentWorkspace?.id,
    viewScope,
    currentTabId
  );

  const {
    activeLayout,
    updateLayoutSlots,
    assignDashboardToSlot,
    addSlot,
    unassignSlot,
    removeSlot,
  } = useDashboardLayouts();
  const [showAddFlyout, setShowAddFlyout] = useState(false);
  const [slotContext, setSlotContext] = useState<SlotContext | null>(null);

  const isLayoutEditable = !!activeLayout && !isSuggestedLayoutId(activeLayout.id);

  // Dashboards instalados que não cabem no layout atual. Hoje o backend
  // marca tudo como `available=false` quando há preferência visível,
  // então esses precisam ser injetados explicitamente no flyout "Adicionar
  // Dashboard" para o usuário re-atribuir a algum slot.
  const unassignedDashboards = useMemo(() => {
    if (!activeLayout) return [];
    const { unassignedDashboards: u } = resolveSlotDashboards(activeLayout, dashboards);
    return u;
  }, [activeLayout, dashboards]);

  const handleSlotPlusClick = useCallback((slot: LayoutSlot) => {
    setSlotContext({ slotId: slot.id, formFactor: slot.formFactor });
    setShowAddFlyout(true);
  }, []);

  const handleAssignBaseToSlot = useCallback(
    async (slotId: string, dashboardBaseId: string) => {
      if (!activeLayout) return;
      if (!isLayoutEditable) {
        toast({
          title: t(
            'dashboards.layouts.suggestedReadOnly',
            'Layout sugerido não pode ser editado. Duplique para personalizar.'
          ),
        });
        return;
      }
      const userDashboard = dashboards.find((d) => d.id === dashboardBaseId);
      let userDashboardId: string;
      if (userDashboard) {
        userDashboardId = userDashboard.user_dashboard_id ?? userDashboard.id;
      } else {
        try {
          const response = await DashboardService.addDashboard({ dashboardBaseId }, scopeContext);
          await queryClient.invalidateQueries({ queryKey: ['dashboards'] });
          await queryClient.invalidateQueries({ queryKey: ['available-dashboards'] });
          userDashboardId = response?.data?.id ?? dashboardBaseId;
        } catch {
          toast({
            title: t('dashboards.addError', 'Erro ao adicionar dashboard'),
            variant: 'destructive',
          });
          return;
        }
      }
      await assignDashboardToSlot(activeLayout.id, slotId, userDashboardId);
    },
    [activeLayout, isLayoutEditable, dashboards, assignDashboardToSlot, queryClient, toast, t]
  );

  // "Remover do slot": esconde a dashboard nas preferences (a dashboard some
  // do `dashboards` retornado por useDashboards → o auto-assignment não a
  // encontra mais; o slot fica vazio). Em layouts custom também limpa o
  // assignment persistido caso existisse. Slot permanece no layout.
  const handleUnassignSlot = useCallback(
    (slotId: string) => {
      if (!activeLayout) return;
      const { assignmentsBySlot } = resolveSlotDashboards(activeLayout, dashboards);
      const dashboardInSlot = assignmentsBySlot.get(slotId);
      if (dashboardInSlot) {
        removeDashboard(dashboardInSlot.id);
      }
      if (!isSuggestedLayoutId(activeLayout.id)) {
        unassignSlot(activeLayout.id, slotId);
      }
    },
    [activeLayout, dashboards, removeDashboard, unassignSlot]
  );

  // "Adicionar Dashboard" sem slot context: garantir que a dashboard
  // recém-marcada-visível tenha onde aparecer. Em layout custom, se nenhum
  // slot existente é compatível com o formFactor da dashboard, criamos um
  // slot novo no final do grid. Em layout sugerido (read-only), o
  // auto-assignment pega se houver slot livre compatível; caso contrário,
  // a dashboard fica em "Adicionar Dashboard" como pendente.
  const handleAddDashboardNoSlot = useCallback(
    async (dashboard: Dashboard) => {
      addDashboard({ dashboardBaseId: dashboard.id });

      if (!activeLayout || isSuggestedLayoutId(activeLayout.id)) return;

      const formFactor = getFormFactor(dashboard.type);
      // Precisamos de slot do mesmo formFactor que esteja VAZIO — não basta
      // existir um do tipo, pois pode já estar ocupado por outra dashboard.
      const { assignmentsBySlot } = resolveSlotDashboards(activeLayout, dashboards);
      const hasEmptyCompatible = activeLayout.slots.some(
        (s) => s.formFactor === formFactor && !assignmentsBySlot.has(s.id)
      );
      if (hasEmptyCompatible) return;

      const size: DashboardSize =
        formFactor === 'hero' ? 'full'
        : formFactor === 'tile' ? 'small'
        : 'medium';
      const w = SIZE_TO_W[size];
      const h = SIZE_TO_H[size];
      const { x, y } = findFirstFitPosition(activeLayout.slots, w, h);
      await addSlot(activeLayout.id, { formFactor, size, x, y, w, h });
    },
    [activeLayout, dashboards, addDashboard, addSlot]
  );

  const handleRemoveSlot = useCallback(
    (slotId: string) => {
      if (!activeLayout) return;
      removeSlot(activeLayout.id, slotId);
    },
    [activeLayout, removeSlot]
  );

  const handleCloseFlyout = () => {
    setShowAddFlyout(false);
    setSlotContext(null);
  };

  const openGenericFlyout = () => {
    setSlotContext(null);
    setShowAddFlyout(true);
  };

  // Skeleton only on the very first app load — when neither prefs (and thus
  // no activeLayout) nor any cached dashboards are available yet. On tab
  // switches the layout is already populated from the optimistic prefs
  // update, so the grid renders its slots immediately and the dashboards
  // fill in as the fetch lands; showing a full skeleton page in that case
  // would just flash. Truly empty tabs (no items, no layout) hit the
  // DashboardEmptyState branch below.
  // Platform scope = superadmin sem org selecionada: não há products, mas deve renderizar o dashboard
  if (products.length === 0 && viewScope !== 'platform') {
    return <NoWorkspacePlaceholder />;
  }

  if (isLoading && !activeLayout && (!dashboards || dashboards.length === 0)) {
    return (
      <div className="flex-1 space-y-4 px-2 pb-8">
        <DashboardSkeleton />
      </div>
    );
  }

  const toolbar = (
    <div className="fixed top-[87px] right-4 z-40 flex flex-col gap-2">
      <Button
        variant="outline"
        size="icon"
        onClick={openGenericFlyout}
        title={t('dashboards.addDashboard')}
        className="bg-background border hover:bg-accent"
      >
        <Edit className="h-4 w-4" />
      </Button>
      <LayoutSwitcher />
    </div>
  );

  if ((!dashboards || dashboards.length === 0) && !activeLayout) {
    return (
      <>
        <div className="flex-1 space-y-4 px-2 pb-8">
          <DashboardTabBar />
          <DashboardEmptyState onAddDashboard={openGenericFlyout} />
        </div>
        {toolbar}
        <AddDashboardFlyout
          isOpen={showAddFlyout}
          onClose={handleCloseFlyout}
          workspaceId={currentWorkspace?.id}
          slotContext={slotContext}
          onAssignToSlot={handleAssignBaseToSlot}
        />
      </>
    );
  }

  return (
    <>
      <div className="flex-1 space-y-4 px-2 pb-8">
        <DashboardTabBar />
        <DashboardGrid
          dashboards={dashboards}
          onUpdateDashboard={updateDashboard}
          onRemoveDashboard={removeDashboard}
          onReorderDashboards={reorderDashboards}
          activeLayout={activeLayout}
          onUpdateLayoutSlots={updateLayoutSlots}
          onSlotPlusClick={handleSlotPlusClick}
          onDropDashboardOnSlot={handleAssignBaseToSlot}
          onRemoveSlot={handleRemoveSlot}
          onUnassignSlot={handleUnassignSlot}
          isLayoutEditable={isLayoutEditable}
        />
      </div>

      {toolbar}

      <AddDashboardFlyout
        isOpen={showAddFlyout}
        onClose={handleCloseFlyout}
        workspaceId={currentWorkspace?.id}
        slotContext={slotContext}
        onAssignToSlot={handleAssignBaseToSlot}
        onAddNoSlot={handleAddDashboardNoSlot}
        extraDashboards={unassignedDashboards}
      />
    </>
  );
};

export const DashboardView = () => (
  <DashboardDragProvider>
    <DashboardViewBody />
  </DashboardDragProvider>
);
